import { updatePreferences } from '../actions';
import { selectPreferences } from '../selectors';
import { selectCurrentWorkspace, selectCurrentUser } from 'store/Faraday/selectors';

export const SAVE_DASHBOARD_LAYOUT_START = 'SAVE_DASHBOARD_LAYOUT_START';
export const SAVE_DASHBOARD_LAYOUT_END = 'SAVE_DASHBOARD_LAYOUT_END';

/**
 * Preference update thunk for dashboard layout.
 * Pass { showLoading: true } when saving from outside the dashboard (e.g. preferences editor)
 * so the dashboard shows a loading overlay while the API call completes.
 *
 * @param {string} type - Dashboard type (CONTINUOUS_SCANNING | PENTEST | GENERAL)
 * @param {WidgetConfig[]} widgets - Persistent configuration for the dashboard widgets
 * @param {{ showLoading?: boolean }} options
 * @returns {Function} - Redux Thunk
 */
export function saveDashboardLayout (type, widgets, options = {}) {
  return async (dispatch) => {
    if (options.showLoading) dispatch({ type: SAVE_DASHBOARD_LAYOUT_START });
    try {
      await dispatch(updatePreferences({
        dashboard: {
          [type]: {
            widgets
          }
        }
      }));
    } finally {
      if (options.showLoading) dispatch({ type: SAVE_DASHBOARD_LAYOUT_END });
    }
  };
}

/**
 * Set a custom filter for a specific widget
 *
 * @param {string} widgetId - Widget identifier (e.g. 'bySeverity', 'generalLatestFindings')
 * @param {Object} filterObject - Filter configuration object
 * @returns {Function} - Redux Thunk
 */
export function setCustomFilter (widgetId, filterObject) {
  return (dispatch, getState) => {
    const state = getState();
    const workspace = selectCurrentWorkspace(state);
    const user = selectCurrentUser(state);
    const preferences = selectPreferences(state);

    if (!workspace || !user) {
      return Promise.reject(new Error('Workspace or user not found'));
    }

    const workspaceId = workspace;
    const userId = user.id;

    const existingCustomFilters = preferences.dashboard?.customFilters || {};
    const workspaceFilters = existingCustomFilters[workspaceId] || {};
    const userFilters = workspaceFilters[userId] || {};

    const newCustomFilters = {
      ...existingCustomFilters,
      [workspaceId]: {
        ...workspaceFilters,
        [userId]: {
          ...userFilters,
          [widgetId]: filterObject
        }
      }
    };

    return dispatch(updatePreferences({
      dashboard: {
        ...preferences.dashboard,
        customFilters: newCustomFilters
      }
    }));
  };
}

/**
 * Clear a custom filter for a specific widget
 *
 * @param {string} widgetId - Widget identifier
 * @returns {Function} - Redux Thunk
 */
export function clearCustomFilter (widgetId) {
  return (dispatch, getState) => {
    const state = getState();
    const workspace = selectCurrentWorkspace(state);
    const user = selectCurrentUser(state);
    const preferences = selectPreferences(state);

    if (!workspace || !user) {
      return Promise.reject(new Error('Workspace or user not found'));
    }

    const workspaceId = workspace;
    const userId = user.id;

    const existingCustomFilters = preferences.dashboard?.customFilters || {};
    const workspaceFilters = existingCustomFilters[workspaceId] || {};
    const userFilters = workspaceFilters[userId] || {};

    const { [widgetId]: _removed, ...remainingFilters } = userFilters;

    const newCustomFilters = {
      ...existingCustomFilters,
      [workspaceId]: {
        ...workspaceFilters,
        [userId]: remainingFilters
      }
    };

    return dispatch(updatePreferences({
      dashboard: {
        ...preferences.dashboard,
        customFilters: newCustomFilters
      }
    }));
  };
}
